import React, { useState, useContext, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListSubheader from '@mui/material/ListSubheader';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import ImportExportRoundedIcon from '@mui/icons-material/ImportExportRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import UserContext from '../../App/Contexts/UserContext';
import ImgAuth from '../img-with-auth/ImgAuth';
import FileUploadButton from '../presentation-components/FileUpload';
import UploadTile from './UploadTile';
import imageQItems from './imageQItems';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import RoomService from '../../services/RoomService';
import DeleteConfirm from './DeleteConfirm';
import UserService from '../../services/UserService';
import { lightPlate, slideControlSx } from './slideControlTheme';

// The circular controls that sit over a thumbnail's bottom-right corner. A
// horizontal thumbnail is only 150px wide, so up to three of these have to be
// smaller there to leave the uploader's name beside them something to sit in.
//
// The hover and focus states are `lightPlate`, shared with the reactions that
// float over the slide itself; the padding and the icon colour are this
// drawer's own.
const slideActionSx = (horizontal) => ({
    ...slideControlSx(lightPlate),
    padding: horizontal ? '2px' : '4px',
    color: 'inherit' // IconButton would otherwise mute the icons to 54% black
});

export default function ({
    onClose,
    slides, // Objects with the following: { itemId, itemName, mediaType, uploadTime, uploader, visible, index }
    loadingSlides = [], // Listed slides whose image is still downloading
    uploads, // useFileUploads() from the room, so an upload outlives this drawer
    sortAsc = true,
    imgClick,
    onSort,
    onDelete,
    roomId,
    loadedImages,
    totalImages,
    adminIds, // need admin IDs to control if you can delete this image or not,
    roomOwnerId,
    horizontal = false, // When true, renders slides in horizontal scrolling layout
    readOnly = false,
    canChat = false, // Whether this user may post a chat, which is what the compose buttons need
    onCompose // (slide, question) => void; jumps to the composer tagged with that slide
}) {
    const [slideToDelete, setSlideToDelete] = useState(null);
    const { userId } = useContext(UserContext);
    const uniqueIds = useRef(new Set());
    const users = useRef([]); // list of all users involved in these slides to show the names
    const { uploads: pendingUploads = [], start: startUploads, retry, dismiss } = uploads || {};
    const items = imageQItems({ slides, loadingSlides, uploads: pendingUploads, sortAsc });

    useEffect(() => {
        if (readOnly || !slides) return;
        const newIds = slides.map((s) => s.uploader).filter((id) => id && !uniqueIds.current.has(id));
        if (newIds.length) {
            newIds.forEach((id) => uniqueIds.current.add(id));
            Promise.all(newIds.map(UserService.getUserById)).then((returnedUsers) => {
                users.current = users.current.concat(returnedUsers);
            });
        }
    }, [slides]);

    // The compose buttons are gated on being able to post a chat at all, which
    // is a different question from who may delete a slide. Guests never get
    // them: their chat panel has no composer to jump to.
    const canCompose = !readOnly && canChat && !!onCompose;
    const canDelete = (slide) =>
        !readOnly && (adminIds.includes(userId) || userId === slide.uploader);

    // The frame every Image Q tile shares, so a slide still downloading or an
    // upload in progress takes exactly the space its slide will.
    const tileStyle = {
        paddingTop: horizontal ? 0 : 'calc(9 / 16 * 100%)',
        height: horizontal ? '100%' : 0,
        width: horizontal ? '150px' : '100%',
        minWidth: horizontal ? '150px' : undefined,
        position: 'relative',
        overflow: 'hidden',
        background: '#aaa',
        border: 'solid black 3px',
        flexShrink: 0
    };

    const slideNumberStyle = {
        position: 'absolute',
        zIndex: 1,
        bottom: 0,
        left: 0,
        justifyContent: 'center',
        padding: '4px',
        fontWeight: 'bolder',
        fontSize: horizontal ? '1em' : '1.3em',
        WebkitTextStroke: '1px black',
        WebkitTextFillColor: 'white'
    };

    // A slide the room has listed but this browser hasn't downloaded yet. It
    // has its number, but nothing to click or act on until the image arrives.
    const renderLoadingSlide = (slide, key) => (
        <div key={key} style={tileStyle} aria-label={`Slide ${slide.index} loading`}>
            <div style={slideNumberStyle}>{slide.index}</div>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={horizontal ? 20 : 28} sx={{ color: 'rgba(255, 255, 255, 0.9)' }} />
            </Box>
        </div>
    );

    const renderUpload = (upload, key) => (
        <div key={key} style={tileStyle}>
            <UploadTile upload={upload} onRetry={retry} onDismiss={dismiss} horizontal={horizontal} />
        </div>
    );

    const renderItem = (item) => {
        if (item.type === 'upload') return renderUpload(item.upload, item.key);
        if (item.type === 'loading') return renderLoadingSlide(item.slide, item.key);
        return renderSlide(item.slide, item.key);
    };

    // Render a single slide thumbnail
    const renderSlide = (slide, key) => (
        <div key={key} style={tileStyle}>
            {/* The uploader's name and the controls share one bottom row, so
                the controls can never cover the name: pinned bottom-right on
                their own, three of them took most of a horizontal thumbnail,
                and it is the participant-uploaded slides of an Image Q room
                that show a name at all. The row is click-through so the image
                under it stays as clickable as it was. */}
            <div
                style={{
                    position: 'absolute',
                    zIndex: 1,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                    pointerEvents: 'none'
                }}>
                {roomOwnerId !== slide.uploader &&
                users.current.find((u) => u.userId === slide.uploader) ? (
                    <div
                        style={{
                            flex: 1,
                            minWidth: 0,
                            justifyContent: 'center',
                            // Left clearance for the slide number in the same
                            // corner; the controls take their own room now.
                            padding: horizontal ? '0 4px 0 20px' : '0 4px 0 35px',
                            fontWeight: 'bolder',
                            fontSize: horizontal ? '0.8em' : '1.2em',
                            fontFamily: 'Comfortaa',
                            background: 'rgba(170, 170, 170, 0.5)',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            WebkitTextFillColor: 'black',
                            pointerEvents: 'auto'
                        }}>
                        {`${
                            users.current.find((u) => u.userId === slide.uploader)
                                ?.firstName
                        } ${
                            users.current.find((u) => u.userId === slide.uploader)?.lastName
                        }`}
                    </div>
                ) : null}

                {/* One row rather than three separately placed buttons, so they
                    stay aligned however many of them this user gets. */}
                {canCompose || canDelete(slide) ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            gap: '4px',
                            pointerEvents: 'auto'
                        }}>
                        {canCompose ? (
                            <>
                                {/* Tooltip outside the button, so its hover
                                    target is the whole control rather than the
                                    icon inside the padding ring. */}
                                <Tooltip title='Chat About This Slide'>
                                    <IconButton
                                        aria-label={`Chat about slide ${slide.index}`}
                                        sx={slideActionSx(horizontal)}
                                        onClick={(e) => {
                                            // Without this the press also makes
                                            // the slide active, which is not
                                            // what the button says it does.
                                            e.stopPropagation();
                                            onCompose(slide, false);
                                        }}>
                                        <ChatBubbleOutlineRoundedIcon
                                            fontSize={horizontal ? 'small' : 'medium'}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title='Ask About This Slide'>
                                    <IconButton
                                        aria-label={`Ask a question about slide ${slide.index}`}
                                        sx={slideActionSx(horizontal)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCompose(slide, true);
                                        }}>
                                        <HelpOutlineRoundedIcon
                                            fontSize={horizontal ? 'small' : 'medium'}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : null}
                        {canDelete(slide) ? (
                            <Tooltip title='Delete Slide'>
                                <IconButton
                                    sx={slideActionSx(horizontal)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSlideToDelete(slide);
                                    }}>
                                    <DeleteRoundedIcon
                                        fontSize={horizontal ? 'small' : 'medium'}
                                    />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div style={slideNumberStyle}>{slide.index}</div>
            <ImgAuth
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                }}
                presentationDrawer={true}
                slide={slide}
                imgClick={readOnly ? undefined : (img) => {
                    try {
                        console.log('Slide clicked in drawer:', {
                            index: slide.index,
                            itemId: slide.itemId,
                            hasBurl: !!slide.burl
                        });
                        RoomService.setActiveImageInPresentation(roomId, slide.itemId).catch(
                            (e) => console.error('Error setting active image:', e.message)
                        );
                        if (imgClick) {
                            imgClick(img);
                        }
                        if (onClose) {
                            onClose();
                        }
                    } catch (error) {
                        console.error('Error in slide click handler:', error);
                    }
                }}></ImgAuth>
        </div>
    );

    // Horizontal layout for small screens
    if (horizontal) {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <DeleteConfirm
                    open={slideToDelete}
                    onCancel={() => setSlideToDelete(null)}
                    onConfirm={() => {
                        onDelete(slideToDelete);
                        setSlideToDelete(null);
                    }}
                />
                {/* Header with upload and sort buttons */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!readOnly && <FileUploadButton onFiles={startUploads} />}
                        <Typography variant="body2">Image Q</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {loadedImages < totalImages && (
                            <Typography variant="caption" color="textSecondary">
                                {`${loadedImages}/${totalImages}`}
                            </Typography>
                        )}
                        <ButtonBase
                            style={{
                                borderRadius: '50%',
                                justifyContent: 'center',
                                padding: '4px'
                            }}
                            onClick={onSort}>
                            <Tooltip title='Reverse Order'>
                                <ImportExportRoundedIcon />
                            </Tooltip>
                        </ButtonBase>
                    </Box>
                </Box>
                {/* Horizontal scrolling slides */}
                <Box sx={{
                    flex: 1,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 1,
                    px: 1,
                    py: 1
                }}>
                    {items.map(renderItem)}
                </Box>
            </Box>
        );
    }

    // Vertical layout (default)
    return (
        <List dense>
            <DeleteConfirm
                open={slideToDelete}
                onCancel={() => setSlideToDelete(null)}
                onConfirm={() => {
                    onDelete(slideToDelete);
                    setSlideToDelete(null);
                }}
            />
            {/* Z-index to overlap the trash-can icons */}
            {!readOnly && (
                <ListSubheader
                    disableGutters
                    style={{ background: 'white', zIndex: 2, paddingTop: '20px' }}>
                    <Grid container justifyContent='space-between'>
                        <Grid item xs style={{ textAlign: 'center' }}>
                            <ListItemIcon style={{ justifyContent: 'center' }}>
                                <FileUploadButton onFiles={startUploads} />
                            </ListItemIcon>
                        </Grid>
                    </Grid>
                </ListSubheader>
            )}
            <Divider />
            <ListItem disableGutters>
                <Grid container justifyContent='space-around'>
                    <Grid item xs={9}>
                        <ListItemText primary='Image Q' align='center' />
                    </Grid>
                    <Grid item xs={3}>
                        <ButtonBase
                            style={{
                                borderRadius: '50%',
                                justifyContent: 'center',
                                padding: '4px'
                            }}
                            onClick={onSort}>
                            <Tooltip title='Reverse Order'>
                                <ImportExportRoundedIcon />
                            </Tooltip>
                        </ButtonBase>
                    </Grid>
                </Grid>
            </ListItem>
            {loadedImages < totalImages ? (
                <ListItem>
                    <Box display='flex' alignItems='center' width='100%'>
                        <Box width='100%'>
                            <LinearProgress
                                style={{ width: '100%' }}
                                variant='determinate'
                                value={(loadedImages / totalImages) * 100}
                            />
                            <Typography
                                variant='body2'
                                color='textSecondary'>{`${loadedImages} of ${totalImages}`}</Typography>
                        </Box>
                    </Box>
                </ListItem>
            ) : null}
            {/* Iterate the different image slides and display them as thumbnails */}
            {items.map((item) => (
                <ListItem key={item.key} disableGutters>
                    {renderItem(item)}
                </ListItem>
            ))}
        </List>
    );
}
